import React from 'react';
import { FormError, Switch, SwitchProps } from 'pounceV2';
import { FieldConfig, useField } from 'formik';
import { safeguardErrorMsg } from '../utils';

/** Coerce Formik field values that should be boolean (handles occasional API/cast quirks).
 * For example, if a Switch is used with an undefined initial value, Formik defaults it to an array
 * when the value is set to true (so it looks like ['on']). We want to coerce it to a boolean, otherwise
 * when toggling the switch off, it sets the value to an empty array which is truthy, so the switch stays on.
 */
export function formikSwitchCheckedValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value);
}

export const FormikSwitchV3: React.FC<
  SwitchProps & Required<Pick<FieldConfig, 'name'>>
> = props => {
  const { onChange: onChangeProp, ...rest } = props;
  const [field, meta, helpers] = useField(props.name);
  const isInvalid = meta.touched && !!meta.error;
  const errorElementId = isInvalid ? `${props.name}-error` : undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    // Caller replaced Formik's default handler (confirm modals, submitForm, compose with
    // useField from parent, etc.) — they own syncing Formik.
    if (onChangeProp != null && onChangeProp !== field.onChange) {
      onChangeProp(event, checked);
      return;
    }
    // Default path (no onChange) or `<Field as={FormikSwitchV3} />` where onChange is
    // Formik's field.onChange: sync from MUI's boolean. field.onChange(event) alone is
    // unreliable for MUI Switch events vs nested field paths.
    helpers.setValue(checked);
  };

  return (
    <>
      <Switch
        {...rest}
        checked={formikSwitchCheckedValue(field.value)}
        onChange={handleChange}
        invalid={isInvalid}
        aria-describedby={isInvalid ? errorElementId : undefined}
      />
      {isInvalid && (
        <FormError mt={1} id={errorElementId}>
          {safeguardErrorMsg(meta.error)}
        </FormError>
      )}
    </>
  );
};
